//--- React
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
//--- Ant Design
import { Card, Col, Divider, Row, Select, Spin } from "antd";
//--- Project
import "./styles.css";
import { setPageLoader } from "reducers/loaderSlice";
import TreeMap from "components/Common/Charts/TreeMap";
import {
  getCompanyCategoryUrl,
  getCompanySubCategoryUrl,
} from "apis/competitorPulseApis";
import APIError from "components/Common/APIError";
import BarChartCompetitorReview from "./BarChart";
import { generateColorShades } from "components/Common/Utils/graphUtils";

const ReviewAnalysis = ({
  reviewCount,
  filterValue,
  onFilterChange,
  companyData,
  companyDataLoading,
  companyDataIsError,
  companyDataError,
  selectedColor,
  setSubCatgryColor,
}) => {
  const dispatch = useDispatch();
  const [categoryList, setCategoryList] = useState(null);

  const {
    data: categoryData,
    isLoading: categoryDataLoading,
    isError: categoryDataIsError,
    error: categoryDataError,
  } = useQuery({
    queryKey: [
      "categoryCount",
      filterValue.company,
      filterValue.startDate,
      filterValue.endDate,
      filterValue.sourceList,
      filterValue.bikeModel,
      filterValue.sentiment,
    ],
    queryFn: () =>
      getCompanyCategoryUrl(
        filterValue.company,
        Math.floor(filterValue.startDate / 1000),
        Math.floor(filterValue.endDate / 1000),
        filterValue.sourceList,
        filterValue.bikeModel,
        filterValue.sentiment
      ),
    keepPreviousData: true,
    enabled: !!(
      filterValue.company &&
      filterValue.startDate &&
      filterValue.endDate
    ),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    dispatch(setPageLoader(null));
    if (categoryData?.category_count) {
      setCategoryList(
        Object.entries(categoryData.category_count).map(
          ([key, value], index) => ({
            id: index + 1,
            parent: null,
            name: key,
            value: value["percentage"],
            color: filterValue?.category === key ? selectedColor : undefined,
          })
        )
      );
    } else {
      setCategoryList([]);
    }
  }, [
    categoryData,
    setCategoryList,
    dispatch,
    filterValue?.category,
    selectedColor,
  ]);

  const {
    data: subCategoryData,
    isLoading: subCategoryDataLoading,
    isError: subCtegoryDataIsError,
    error: subCategoryDataError,
  } = useQuery({
    queryKey: [
      "subCategoryCount",
      filterValue.company,
      filterValue.startDate,
      filterValue.endDate,
      filterValue.category,
    ],
    queryFn: () =>
      getCompanySubCategoryUrl(
        filterValue.company,
        Math.floor(filterValue.startDate / 1000),
        Math.floor(filterValue.endDate / 1000),
        filterValue.sourceList,
        filterValue.bikeModel,
        filterValue.sentiment,
        filterValue.category
      ),
    keepPreviousData: true,
    enabled: !!(
      filterValue.company &&
      filterValue.startDate &&
      filterValue.endDate &&
      filterValue.category
    ),
    refetchOnWindowFocus: false,
  });

  useMemo(() => {
    if (subCategoryData?.sub_category_percentage && filterValue?.category) {
      const barColors = generateColorShades(
        "200",
        Object.keys(subCategoryData?.sub_category_percentage).length
      );
      setSubCatgryColor(
        Object.fromEntries(
          Object.entries(subCategoryData?.sub_category_percentage || {})
            .sort((a, b) => b[1] - a[1])
            .map(([key], index) => [
              key,
              filterValue?.subCategory
                ? key === filterValue?.subCategory
                  ? selectedColor
                  : "transparent"
                : barColors[index],
            ])
        )
      );
    } else {
      setSubCatgryColor({});
    }
  }, [
    subCategoryData,
    setSubCatgryColor,
    filterValue?.category,
    filterValue?.subCategory,
    selectedColor,
  ]);

  return (
    <Card
      title={<div className="sub-header">Review Analysis</div>}
      bordered={false}
    >
      <Row
        gutter={[16, 16]}
        justify={"space-around"}
        className="review-analysis-box"
      >
        <Col xs={24} sm={12} md={6} lg={5} xl={4}>
          <div className="lable">Model</div>
          <Select
            allowClear
            size="large"
            mode="multiple"
            placeholder="Select a Model"
            data-testid="market-model-dropdown"
            className="w100 mt05"
            value={filterValue.bikeModel || [null]}
            loading={companyDataLoading}
            maxTagCount={"responsive"}
            onChange={(value) => onFilterChange("bikeModel", value, companyData?.bike_models?.length)}
          >
            <Select.Option value={null}>All</Select.Option>
            {companyData?.bike_models?.map((items, index) => (
              <Select.Option key={index} value={items}>
                {items}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5} xl={4}>
          <div className="lable">Source</div>
          <Select
            allowClear
            size="large"
            mode="multiple"
            placeholder="Select a Source"
            data-testid="market-source-dropdown"
            className="w100 mt05"
            value={filterValue.sourceList || [null]}
            loading={companyDataLoading}
            maxTagCount={"responsive"}
            onChange={(value) => onFilterChange("sourceList", value, companyData?.source?.length)}
          >
            <Select.Option value={null}>All</Select.Option>
            {companyData?.source?.map((items, index) => (
              <Select.Option key={index} value={items}>
                {items}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5} xl={4}>
          <div className="lable">Sentiment</div>
          <Select
            allowClear
            size="large"
            placeholder="Select a sentiment"
            data-testid="market-sentiment-dropdown"
            className="w100 mt05"
            loading={companyDataLoading}
            value={filterValue.sentiment || null}
            maxTagCount={"responsive"}
            onChange={(value) => onFilterChange("sentiment", value)}
          >
            <Select.Option value={null}>All</Select.Option>
            {companyData?.sentiments?.map((items, index) => (
              <Select.Option
                key={index}
                value={items}
                className="text-capitalize"
              >
                {items}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={4} lg={7} xl={10}>
          <div className="flex-end h100 category-review-count">
            {reviewCount || 0} {"Review" + (reviewCount > 1 ? "s" : "")}
          </div>
        </Col>
      </Row>
      {companyDataIsError || categoryDataIsError ? (
        <Row className="tree-map ml1">
          <APIError
            message={
              companyDataError?.response?.data?.message ||
              categoryDataError?.response?.data?.message
            }
          />
        </Row>
      ) : categoryDataLoading ? (
        <div className="tree-map flex-center">
          <Spin tip="Loading" size="large" />
        </div>
      ) : (
        <Row className="tree-map">
          <Col
            xs={24}
            sm={24}
            md={filterValue.category ? 6 : 24}
            lg={filterValue.category ? 9 : 24}
            xl={filterValue.category ? 10 : 24}
          >
            <Row justify={"center"} align={"middle"} className="plr05">
              <Col xs={24} sm={24}>
                <TreeMap
                  treeData={categoryList}
                  extraMargin={filterValue.category ? null : 40}
                  onClick={(value) => onFilterChange("category", value)}
                />
              </Col>
            </Row>
          </Col>
          <Col
            xs={0}
            sm={0}
            md={filterValue.category ? 1 : 0}
            lg={filterValue.category ? 1 : 0}
            xl={filterValue.category ? 1 : 0}
            className="h100vh"
          >
            <div className="flex-center h100">
              <Divider type="vertical" className="h100" orientation="right" />
            </div>
          </Col>
          <Col
            xs={filterValue.category ? 24 : 0}
            sm={filterValue.category ? 24 : 0}
            md={filterValue.category ? 17 : 0}
            lg={filterValue.category ? 14 : 0}
            xl={filterValue.category ? 13 : 0}
          >
            <Row justify={"center"} align={"middle"} className="h100 plr05">
              <Col xs={24} md={24} className="h100">
                {subCtegoryDataIsError ? (
                  <APIError
                    message={subCategoryDataError?.response?.data?.message}
                  />
                ) : subCategoryDataLoading ? (
                  <Row justify={"center"} align={"middle"} className="h100">
                    <Spin tip="Loading" size="large" />
                  </Row>
                ) : (
                  <BarChartCompetitorReview
                    data={subCategoryData?.sub_category_percentage || {}}
                    seriesName={filterValue?.category}
                    selectedColor={selectedColor}
                    selectedSubCategory={filterValue?.subCategory}
                    setSubCategory={(value) =>
                      onFilterChange("subCategory", value)
                    }
                  />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </Card>
  );
};

export default ReviewAnalysis;
